<template>
  <div>
    <div
      class="d-flex align-items-start flex-wrap justify-content-between mb-2"
    >
      <detail-label
        label="Scheduled:"
        :value="scheduledLabel"
        class="mb-1 whitespace-nowrap"
        data-cy="scheduled-label"
      ></detail-label>
      <div class="d-flex flex-column align-items-end">
        <div v-if="allowDoNotNotify">
          <Checkbox
            v-model="doNotNotify"
            label="Do not notify customer"
            class="do-not-notify whitespace-nowrap mb-2"
          ></Checkbox>
        </div>
        <div
          v-if="warningMessage && !technicianId"
          class="warning-message ml-2 whitespace-nowrap"
          data-cy="schedule-warning-message"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          {{ warningMessage }}
        </div>
      </div>
    </div>
    <div
      class="flex flex-wrap md:flex-row items-center gap-4"
      :class="{ 'has-error': fieldError }"
    >
      <Calendar
        v-model="day"
        class="flex-1 flex-shrink"
        :disabled-dates="disabledDates"
        :min-date="new Date()"
        @month-change="handleMonthChange"
      ></Calendar>
      <div class="slots-cont w-full flex-1 flex-grow">
        <TimeSlots
          v-if="timeSlots.length"
          v-model="selectedSlot"
          :time-slots="timeSlots"
          :timezone="timezone"
          :loading="loadingTimeSlots"
          @warning="setWarningMessage"
          @unavailable="setUnavailable"
        ></TimeSlots>
        <div v-else class="no-results-message">
          <i class="fas fa-calendar-alt"></i>
          Select a Date to View Availability
        </div>
      </div>
    </div>
    <span v-if="fieldError" class="text-danger error-text">
      A Date and Time must be selected
    </span>
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onMounted, Ref, ref, watch } from "vue";
import { useStore } from "vuex";

import Calendar from "@/components/Calendar.vue";
import Checkbox from "@/components/Checkbox.vue";
import DetailLabel from "@/components/DetailLabel.vue";
import TimeSlots from "@/components/TimeSlots.vue";
import { scheduledDateTime } from "@/mixins";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

const DATE_FORMAT = "YYYY-MM-DD";

const store = useStore();

const emit = defineEmits(["update:data", "unavailable", "do-not-notify"]);

interface Props {
  allowDoNotNotify?: boolean;
  defaultDay?: string | null;
  serviceCategoryId?: number | null;
  technicianId?: number | null;
  technicianIds?: number[] | null;
  durationMinutes?: number | null;
  timezone?: string;
  postalCode?: string | number | null;
  scheduledTime?: string;
  arrivalWindowStart?: string;
  arrivalWindowEnd?: string;
  validationErrors?: {
    first: () => void;
  };
}

const props = withDefaults(defineProps<Props>(), {
  allowDoNotNotify: false,
  defaultDay: null,
  serviceCategoryId: null,
  technicianId: null,
  technicianIds: null,
  durationMinutes: null,
  timezone: "",
  postalCode: null,
  scheduledTime: () => new Date().toISOString(),
  arrivalWindowStart: "",
  arrivalWindowEnd: "",
  validationErrors: () => ({
    first: () => {}
  })
});

const initialMonth = moment().month();

interface SelectedSlot {
  scheduled_time: string | null;
  arrival_window_start: string | null;
  arrival_window_end: string | null;
}

const day = ref<string | null>(props.scheduledTime || props.defaultDay);
const month: Ref<number> = ref(initialMonth);
const disabledDates = ref<Date[]>([]);
const queriedMonths = ref<number[]>([]);
const timeSlots = ref([]);
const loadingTimeSlots = ref(false);
const warningMessage = ref("");
const unavailable = ref(false);
const doNotNotify = ref(false);
const selectedSlot = ref<SelectedSlot>({
  scheduled_time: props.scheduledTime,
  arrival_window_start: props.arrivalWindowStart,
  arrival_window_end: props.arrivalWindowEnd
});

onMounted(() => {
  if (day.value) {
    getMonthAvailability();
    getDateTimeSlots();
  }
});

const validationRules = {
  selectedSlot: {
    scheduled_time: { required }
  }
};

const v$ = useVuelidate(
  validationRules,
  { selectedSlot },
  { $autoDirty: true }
);

const scheduledLabel = computed(() => {
  const { scheduled_time, arrival_window_start, arrival_window_end } =
    selectedSlot.value;
  return scheduled_time
    ? scheduledDateTime(
        scheduled_time,
        arrival_window_start,
        arrival_window_end
      )
    : "--";
});

const fieldError = computed(() => {
  return v$.value.selectedSlot.scheduled_time?.$error;
});

watch(day, (newValue, oldValue) => {
  const newValueMoment = moment(newValue as string);
  const oldValueMoment = moment(oldValue as string);
  if (newValueMoment !== oldValueMoment && newValue) {
    selectedSlot.value.scheduled_time = null;
    selectedSlot.value.arrival_window_start = "";
    selectedSlot.value.arrival_window_end = "";
    getDateTimeSlots();
  }
});

watch(month, (newValue: number, oldValue: number) => {
  if (newValue !== oldValue) getMonthAvailability();
});

watch(
  () => props.serviceCategoryId,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) refreshAvailability();
  }
);

watch(
  () => props.technicianId,
  (newValue, oldValue) => {
    if (newValue !== oldValue) refreshAvailability();
  }
);

watch(
  () => props.technicianIds,
  (newValue, oldValue) => {
    if (newValue !== oldValue) refreshAvailability();
  }
);

watch(
  () => props.durationMinutes,
  (newValue, oldValue) => {
    if (newValue !== oldValue) refreshAvailability();
  }
);

watch(unavailable, (newValue, oldValue) => {
  if (newValue !== oldValue) emit("unavailable", newValue);
});

watch(
  () => selectedSlot.value,
  ({ scheduled_time, arrival_window_start, arrival_window_end }) => {
    emit("update:data", { key: "scheduled_time", value: scheduled_time });
    emit("update:data", {
      key: "arrival_window_start",
      value: arrival_window_start
    });
    emit("update:data", {
      key: "arrival_window_end",
      value: arrival_window_end
    });
  }
);

watch(doNotNotify, (val) => {
  emit("do-not-notify", val);
});

const validate = () => {
  return v$.value.$validate();
};

function buildTechParams(): Record<string, unknown> {
  if (props.technicianIds && props.technicianIds.length > 1) {
    return { user_ids: props.technicianIds };
  }
  const singleId =
    props.technicianIds?.length === 1
      ? props.technicianIds[0]
      : props.technicianId;
  return singleId ? { user_id: singleId } : {};
}

function getMonthAvailability() {
  // cache month requests
  if (!queriedMonths.value.includes(month.value)) {
    queriedMonths.value.push(month.value);
    fetchMonthAvailability({
      service_date: moment.utc().month(month.value).format(DATE_FORMAT),
      service_category_id: props.serviceCategoryId,
      duration_minutes: props.durationMinutes,
      ...buildTechParams(),
      postal_code: props.postalCode
    }).then((res) => {
      const disabledDatesFilter = Object.keys(res.data)
        .filter((date) => !res.data[date])
        .map((date) => moment(date).toDate());
      disabledDates.value = disabledDates.value.concat(disabledDatesFilter);
    });
  }
}

function getDateTimeSlots() {
  loadingTimeSlots.value = true;
  if (!day.value) return;
  fetchTimeSlots({
    service_date: moment(day.value).utc().format(DATE_FORMAT),
    service_category_id: props.serviceCategoryId,
    duration_minutes: props.durationMinutes,
    ...buildTechParams(),
    postal_code: props.postalCode,
    all_hours: true
  })
    .then((res) => {
      timeSlots.value = res.data;
    })
    .finally(() => (loadingTimeSlots.value = false));
}

function handleMonthChange(newValue) {
  month.value = newValue;
}

function refreshAvailability() {
  timeSlots.value = [];
  disabledDates.value = [];
  queriedMonths.value = [];
  getMonthAvailability();
  if (day.value) getDateTimeSlots();
}

function setWarningMessage(warning) {
  warningMessage.value = warning;
}

function setUnavailable(newValue) {
  unavailable.value = newValue;
}

async function fetchMonthAvailability(payload) {
  return await store.dispatch("fetchMonthAvailability", payload);
}

async function fetchTimeSlots(payload) {
  return await store.dispatch("fetchTimeSlots", payload);
}

defineExpose({
  validate
});

export interface AvailabilityFormInterface {
  validate: () => Promise<boolean>;
}
</script>

<style scoped lang="scss">
.slots-cont {
  position: relative;
  min-width: 350px;
  min-height: 225px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.warning-message {
  display: flex;
  align-items: center;
  background: var(--warning-yellow);
  padding: 5px 10px;
}

.no-results-message > i {
  font-size: 2.2em;
}

.has-error {
  border: 2px solid var(--danger-color);
  background-color: var(--danger-color-shade) !important;
  padding-right: 5px;
  border-radius: 3px;
}

.do-not-notify {
  font-size: 0.9em;
}
</style>
